import { useWebSocketContext } from "apollo/client/WebSocketContext";
import decryptPatcher from "apollo/encryption/decryptPatcher";
import { createEncryptionDefinition } from "apollo/encryption/queryKeys";
import { usePostEncryptionJob } from "apollo/hooks/mutations";
import { useGetEncryptionJobsQuery } from "apollo/hooks/queries";
import { useOnEncryptionJob } from "apollo/hooks/subscriptions";
import { getSessionAccess } from "core/model/crypto/cryptoService";
import { useIsLoggedSealdOnly } from "core/model/patients/encryption/useIsLoggedSealdOnly";
import { convertBirthDateToFhir } from "core/model/utils/dates";
import { isProd } from "core/model/utils/featureFlags";
import { getSealdSDKInstance } from "core/seald";
import {
  DecryptedFhirPatient,
  EncryptionJob,
  EncryptionJobMaterial,
  EncryptionJobRequest,
  FhirEntry,
} from "core/types";
import { cloneDeep } from "lodash";
import { useCallback, useEffect } from "react";
import { useTracking } from "react-tracking";
import { useLoggedCareseeker } from "reduxentities/hooks";

const FHIR_ENCRYPTED_PATIENT_MODEL_DEFINITION = {
  fhir_patient: {
    entry: {
      resource: {
        address: {
          _line: {
            extension: {
              valueString: true,
            },
          },
          houseNumber: true,
        },
        birthDate: true,
        identifier: {
          value: true,
        },
        name: {
          family: true,
          given: true,
        },
        valueQuantity: {
          value: true,
        },
      },
    },
  },
  session_key_context: true,
};

const convertGivenNameToFhir = (
  resource: FhirEntry<string>["resource"],
): string[] | null => {
  if (
    resource?.resourceType === "Patient" &&
    typeof resource?.name?.[0]?.given === "string"
  )
    return resource.name[0].given.split(" ");

  return null;
};

const normalizeEntryForFhir = (entry: FhirEntry<string>): FhirEntry<string> => {
  const resource = entry.resource;

  if (resource?.name?.[0]?.given) {
    const normalizedGivenName = convertGivenNameToFhir(resource);
    if (normalizedGivenName) resource.name[0].given = normalizedGivenName;
  }

  if (resource?.birthDate) {
    const normalizedBirthDate = convertBirthDateToFhir(resource.birthDate);
    if (normalizedBirthDate) resource.birthDate = normalizedBirthDate;
  }

  if (resource?.address?.length) {
    for (const addressIndex in resource.address) {
      const address = resource.address[addressIndex];
      if (address?.line?.length && address?._line?.length) {
        const lineInfoOfTheStreet = address._line[0];
        if (lineInfoOfTheStreet.extension.length > 1) {
          const houseNumberExtension = lineInfoOfTheStreet.extension[1];
          const houseNumberValue = houseNumberExtension.valueString;
          if (address.line[0]) {
            address.line[0] = `${address.line[0]} ${houseNumberValue}`;
          }
        }
      }
    }
  }

  return entry;
};

export const normalizeValuesForFhir = (
  fhirPatient: DecryptedFhirPatient,
): DecryptedFhirPatient => {
  fhirPatient.entry = fhirPatient.entry?.map((entry: FhirEntry<string>) =>
    normalizeEntryForFhir(entry),
  );
  return fhirPatient;
};

export function useEncryptionJobs(privateKey: string | undefined) {
  const { trackEvent } = useTracking();
  const careseeker = useLoggedCareseeker();
  const [wsEncryptionJob] = useOnEncryptionJob();
  const { isAlive } = useWebSocketContext();
  const [postEncryptionJob] = usePostEncryptionJob();
  const isSealdOnly = useIsLoggedSealdOnly();
  const isLogged = !!careseeker; // dep can be removed when we remove isSealdOnly

  const [, encryptionJobs] = useGetEncryptionJobsQuery({
    careseekerId: careseeker?.id ?? -1,
    skip: isAlive !== false,
  });

  const doEncryptionJob = useCallback(
    async (encryptionJob: EncryptionJob) => {
      if (!isProd) {
        console.log("FHIR job received:", {
          encryption_job: encryptionJob ?? {},
        });
      }
      if (!encryptionJob) return;
      const {
        auction_id,
        fhir_patient,
        id,
        patient_encrypted_session_key,
        patient_seald_encryption_context,
        receiver_integration_public_key,
      } = encryptionJob;
      if (!fhir_patient) return;
      if (!auction_id) return;
      if (!receiver_integration_public_key) return;

      if (!patient_encrypted_session_key && !patient_seald_encryption_context)
        return;

      const decryptedSessionKey = await getSessionAccess({
        context: {},
        privateKey: privateKey as string,
        sessionKey: patient_encrypted_session_key ?? null,
        trackEvent,
      });

      let hasEncryptionAccess = isSealdOnly({
        newSealdSession: patient_seald_encryption_context,
        oldSession: patient_encrypted_session_key,
      })
        ? false
        : Boolean(decryptedSessionKey);

      if (patient_seald_encryption_context) {
        try {
          const sealdSDKInstance = await getSealdSDKInstance();
          if (sealdSDKInstance) {
            try {
              const decryptedSealdSession =
                await sealdSDKInstance.retrieveEncryptionSession({
                  sessionId: patient_seald_encryption_context.seald_id,
                });
              hasEncryptionAccess = Boolean(decryptedSealdSession);
            } catch (error) {
              hasEncryptionAccess = false;
              console.error(
                "Seald error getting seald session in encryption job",
                error,
              );
            }
          }
        } catch (error) {
          console.error(
            "Seald error getting seald sdk in encryption job",
            error,
          );
        }
      }

      if (!hasEncryptionAccess) {
        console.error(
          "EncryptionJob can't be fulfilled without encryption access for job id ",
          id,
        );
        return;
      }

      const encryptedData = {
        patient: {
          fhir_patient,
          seald_encryption_context: patient_seald_encryption_context,
          session_key_context: {
            session_key: patient_encrypted_session_key,
          },
        },
      };

      const definition = createEncryptionDefinition("operation", {
        patient: FHIR_ENCRYPTED_PATIENT_MODEL_DEFINITION,
      });

      const decryptedData = (await decryptPatcher({
        data: encryptedData,
        encryptionContext: {
          decrypt: true,
          isSealdOnly,
          privateKey,
          trackEvent,
        },
        encryptionDefinition: definition,
        integrationMode: true,
        mode: "decrypt",
      })) as {
        patient: {
          fhir_patient: DecryptedFhirPatient;
          session_key_context: AnyObject;
        };
      };

      const fhirPatient = normalizeValuesForFhir(
        decryptedData.patient.fhir_patient,
      );

      if (!isProd) {
        console.info(`FHIR decrypted message for auction ${auction_id}: `, {
          fhirMessage: fhirPatient,
        });
      }

      const encryptedJob = await encryptJSON({
        encryptionMaterial: {
          encryption_public_key: receiver_integration_public_key,
        },
        json: JSON.stringify(fhirPatient),
      });

      await postEncryptionJob(encryptedJob, {
        auctionId: auction_id,
        id,
      });
    },
    [trackEvent, postEncryptionJob, isSealdOnly],
  );

  useEffect(() => {
    if (isLogged && encryptionJobs?.length) {
      for (const encryptionJob of encryptionJobs) {
        doEncryptionJob(cloneDeep(encryptionJob)).catch((err) => {
          console.error("EncryptionJobs error: ", err);
        });
      }
    }
  }, [encryptionJobs, doEncryptionJob, isLogged]);

  useEffect(() => {
    if (isLogged && wsEncryptionJob) {
      doEncryptionJob(cloneDeep(wsEncryptionJob)).catch((err) => {
        console.error("EncryptionJob error: ", err);
      });
    }
  }, [doEncryptionJob, wsEncryptionJob, isLogged]);
}

/**
 * Encyrpt JSON
 *
 * 1. Compress json with gzip
 * 2. Sign data w/private key
 * 3. Generate AES SECRETS (GCM key + iv)
 * 4. Encrypt json w/AES SECRETS
 * 5. Encrypt AES SECRETS JSON w/provided Pk
 * 6. Encode encrypted parts in Base64
 */
export async function encryptJSON({
  encryptionMaterial,
  json: unzippedJson,
}: {
  encryptionMaterial: EncryptionJobMaterial;
  json: string;
}): Promise<EncryptionJobRequest> {
  // lazy load heavy deps
  const pako = await import("pako");
  const forge = await import("node-forge");

  // GZIPPING
  const encodedData = pako.gzip(unzippedJson);
  const gzippedJson = Buffer.from(encodedData).toString("binary");

  let signature;
  if (encryptionMaterial.signing_private_key) {
    // SIGNING w/PRIVATE KEY
    const privateKey = forge.pki.privateKeyFromPem(
      encryptionMaterial.signing_private_key,
    );
    const md = forge.md.sha512.create();
    md.update(gzippedJson);
    signature = privateKey.sign(md);
  }

  // AES SECRETS GENERATION
  const key = forge.random.getBytesSync(32); // AES-256
  const iv = forge.random.getBytesSync(16); // 128 bits

  // DATA ENCRYPTION w/AES GCM
  const cipher = forge.cipher.createCipher("AES-GCM", key);
  cipher.start({
    iv: iv,
    tagLength: 128,
  });
  cipher.update(forge.util.createBuffer(gzippedJson));
  cipher.finish();

  const encrypted = cipher.output;
  const tag = cipher.mode.tag;
  const encryptedLoad = Buffer.from(encrypted.data + tag.data, "binary");

  // AES SECRETS ENCODING
  const secrets = {
    base64EncodedIV: Buffer.from(iv, "binary").toString("base64"),
    base64EncodedKey: Buffer.from(key, "binary").toString("base64"),
  };

  // AES SECRETS ENCRYPTION w/PUBLIC KEY
  const publicKey = forge.pki.publicKeyFromPem(
    encryptionMaterial.encryption_public_key,
  );
  const encryptedSecrets = publicKey.encrypt(
    JSON.stringify(secrets),
    "RSA-OAEP",
    {
      md: forge.md.sha256.create(),
      mgf1: {
        md: forge.md.sha1.create(),
      },
    },
  );

  return {
    aes_encrypted_data: encryptedLoad.toString("base64"),
    compressed: true,
    data_signature: signature
      ? Buffer.from(signature, "binary").toString("base64")
      : undefined,
    encrypted_aes_secrets: Buffer.from(encryptedSecrets, "binary").toString(
      "base64",
    ),
  };
}
